import { Fragment, useMemo } from "react";

import { capitalize } from "lodash";
import moment from "moment";

import { clsx } from "@alpacahq/ui/old";

import {
  useGetHYCAccountStatus,
  useGetHYCAccruedInterest,
} from "~/v2/api/hooks/useGetHighYieldCash";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useGetBalances } from "../../../../api/hooks/useGetBalances";
import type { Margin } from "../../../../api/rest/account";
import {
  getMonthlyAccruedInterest,
  getTotalAccruedInterest,
} from "../../highYieldCash/util";

const FIELDS = {
  "Buying Power": {
    regt_buying_power: "RegT Buying Power",
    effective_buying_power: "Effective Buying Power",
    options_buying_power: "Options Buying Power",
    non_marginable_buying_power: "Non-Marginable Buying Power",
  },
  Margin: {
    initial_margin: "Initial Margin",
    maintenance_margin: "Maintenance Margin",
  },
  Cash: {
    cash: "Cash",
    cash_withdrawable: "Cash Withdrawable",
    pending_transfer_out: "Pending Transfer Out",
    pending_transfer_in: "Pending Transfer In",
  },
  "High Yield Cash": {
    cash_accruing_interest: "Cash Accruing Interest",
    monthly_accrued_interest: "Monthly Accrued Interest",
    total_accrued_interest: "Total Accrued Interest",
  },
  Positions: {
    equity: "Equity",
    long_market_value: "Long Market Value",
    short_market_value: "Short Market Value",
    position_market_value: "Position Market Value",
  },
  Miscellaneous: {
    accrued_fees: "Accrued Fees",
    held_ach_deposits: "Held ACH Deposits",
  },
} as const;

const NON_CURRENCY_FIELDS: (keyof Margin)[] = ["held_ach_deposits"];

type FieldFormatProps = {
  value: string | number | boolean | null | undefined;
  isNonCurrency: boolean;
  isFetched: boolean;
};

const getValue = ({
  value,
  isNonCurrency,
  isFetched,
}: FieldFormatProps): string => {
  try {
    // NaN is ok here, we want to know if it's a number
    const asFloat = parseFloat(`${value}`);

    if (!isFetched) {
      return "...";
    } else if (isNonCurrency || Number.isNaN(asFloat)) {
      return `${value}`;
    }

    return asFloat.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  } catch (_error) {
    return `${value}`;
  }
};

export const Balances = () => {
  const { currentAccount, product } = useAccountContext();

  const { data: balanceData, isFetched: isBalanceFetched } = useGetBalances({
    accountID: currentAccount?.id ?? "",
    paper: product === "paper",
    options: {
      enabled: !!currentAccount?.id,
      initialData: {},
    },
  });

  const { hycStatus } = useGetHYCAccountStatus({
    accountID: currentAccount?.id,
    options: { enabled: product !== "paper" },
  });
  const isHYCEnrolled =
    hycStatus === "ACTIVE" || hycStatus === "PENDING_CHANGE";

  const {
    data: hycAccruedInterest = [],
    isFetched: isHYCAccruedInterestFetched,
  } = useGetHYCAccruedInterest({
    accountID: currentAccount?.id,
    options: { enabled: isHYCEnrolled },
  });

  const isHYCDisplayed = useMemo(() => {
    if (product !== "live" || hycStatus === null) {
      return false;
    }

    return true;
  }, [product, hycStatus]);

  // merge Balance and HYC data
  const data = useMemo(() => {
    const lastDate = moment(balanceData?.last?.balance_asof ?? "");

    return {
      current: {
        ...balanceData?.current,
        // Current HYC
        cash_accruing_interest:
          (hycStatus === "ACTIVE" && hycAccruedInterest.at(0)?.balance) || "-",
        monthly_accrued_interest: getMonthlyAccruedInterest({
          hycAccruedInterest,
        }),
        total_accrued_interest: getTotalAccruedInterest({
          hycAccruedInterest,
        }),
      },
      last: {
        ...balanceData?.last,
        // Last HYC
        last_cash_accruing_interest:
          hycAccruedInterest.find((interest) =>
            moment(interest.date).isSame(lastDate, "day"),
          )?.balance ?? "-",
        last_monthly_accrued_interest: getMonthlyAccruedInterest({
          hycAccruedInterest,
          date: lastDate,
        }),
        last_total_accrued_interest: getTotalAccruedInterest({
          hycAccruedInterest,
          date: lastDate,
        }),
      },
    };
  }, [balanceData, hycAccruedInterest, hycStatus]);

  const rows = Object.entries(FIELDS)
    .filter(
      ([sectionTitle]) =>
        !(!isHYCDisplayed && sectionTitle === "High Yield Cash"),
    )
    .map(([sectionTitle, sectionFields], index) => (
      <Fragment key={sectionTitle}>
        <tr>
          <th
            className={clsx(
              "pt-3 pr-3 pb-2 pl-5 text-left font-medium text-base",
              index === 0 && "pt-5",
            )}
          >
            {sectionTitle}
          </th>
        </tr>
        {Object.entries(sectionFields).map(([k, label]) => {
          const key = k as keyof Margin;
          const isNonCurrency = NON_CURRENCY_FIELDS.includes(key);

          const isFetched =
            sectionTitle === "High Yield Cash"
              ? isHYCAccruedInterestFetched
              : isBalanceFetched;

          const lastValue = getValue({
            isFetched,
            isNonCurrency,
            // Some values are not available for display
            // from the last close, so we use a dash instead
            value: data.last[`last_${key}` as keyof Margin] || "-",
          });

          const currentValue = getValue({
            isFetched,
            isNonCurrency,
            value: data.current[key] || "-",
          });

          return (
            <tr key={key}>
              <th className="py-2 pl-5 text-left font-normal">{label}</th>
              <td className="px-3 py-2 text-right text-sm">{lastValue}</td>
              <td className="py-2 pr-3 text-right text-sm">{currentValue}</td>
            </tr>
          );
        })}
      </Fragment>
    ));

  return (
    <div className="mr-8 mb-8 ml-8 xs:ml-0 overflow-clip overflow-x-scroll rounded-lg border border-gray-200 p-0">
      <table className="mb-3 w-full whitespace-nowrap text-gray-900">
        <tbody>
          <tr className="border-gray-200 border-b">
            <th className="py-3 pl-5 text-left font-medium text-base">
              Balance
            </th>
            <th className="px-3 py-3 text-right font-medium">
              {isBalanceFetched ? (
                <>
                  Last Close on{" "}
                  {capitalize(
                    moment(data?.last?.balance_asof).format("MMM Do"),
                  )}
                </>
              ) : (
                "..."
              )}
            </th>
            <th className="py-3 pr-3 text-right font-medium">
              {isBalanceFetched ? "Current" : "..."}
            </th>
          </tr>
          {rows}
        </tbody>
      </table>
    </div>
  );
};

export default Balances;
